import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const AcademicSessionManagement = () => {
  const [academicSessions, setAcademicSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showTermModal, setShowTermModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [expandedSessions, setExpandedSessions] = useState(new Set());

  const sessionSchema = Yup.object().shape({
    academic_session: Yup.string().required('Academic session name is required'),
  });

  const termSchema = Yup.object().shape({
    term: Yup.string().required('Term name is required'),
  });

  useEffect(() => {
    fetchAcademicSessions();
  }, []);

  const fetchAcademicSessions = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAcademicSessions();
      setAcademicSessions(response.data);
    } catch (error) {
      toast.error('Failed to load academic sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSession = async (values, { setSubmitting, resetForm }) => {
    try {
      if (editingSession) {
        await adminAPI.updateAcademicSession(editingSession.id, values);
        toast.success('Academic session updated successfully');
      } else {
        await adminAPI.createAcademicSession(values);
        toast.success('Academic session created successfully');
      }
      
      setShowSessionModal(false);
      resetForm();
      setEditingSession(null);
      fetchAcademicSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveTerm = async (values, { setSubmitting, resetForm }) => {
    try {
      await adminAPI.createTerm(selectedSession.id, values);
      toast.success('Term created successfully');
      setShowTermModal(false);
      resetForm();
      fetchAcademicSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (window.confirm('Are you sure you want to delete this academic session? All associated terms will also be deleted.')) {
      try {
        await adminAPI.deleteAcademicSession(sessionId);
        toast.success('Academic session deleted successfully');
        fetchAcademicSessions();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete session');
      }
    }
  };

  const handleDeleteTerm = async (termId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this term?')) {
      try {
        await adminAPI.deleteTerm(termId);
        toast.success('Term deleted successfully');
        fetchAcademicSessions();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete term');
      }
    }
  };

  const handleUpdateSessionStatus = async (sessionId, status) => {
    try {
      await adminAPI.updateAcademicSessionStatus(sessionId, status);
      toast.success(`Session status updated to ${status}`);
      fetchAcademicSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleUpdateTermStatus = async (termId, status, e) => {
    e.stopPropagation();
    try {
      await adminAPI.updateTermStatus(termId, status);
      toast.success(`Term status updated to ${status}`);
      fetchAcademicSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const toggleSessionExpansion = (sessionId) => {
    const newExpanded = new Set(expandedSessions);
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId);
    } else {
      newExpanded.add(sessionId);
    }
    setExpandedSessions(newExpanded);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Academic Session Management</h1>
          <p className="text-gray-600">Manage academic sessions and terms</p>
        </div>
        <button
          onClick={() => {
            setEditingSession(null);
            setShowSessionModal(true);
          }}
          className="btn-primary flex items-center"
        >
          <i className="fas fa-plus mr-2"></i> Add Academic Session
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Sessions</p>
              <p className="text-2xl font-bold">{academicSessions.length}</p>
            </div>
            <div className="p-3 bg-primary-100 rounded-lg">
              <i className="fas fa-calendar-alt text-primary-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Published Sessions</p>
              <p className="text-2xl font-bold">
                {academicSessions.filter(s => s.academic_session_status === 'published').length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <i className="fas fa-check-circle text-green-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Terms</p>
              <p className="text-2xl font-bold">
                {academicSessions.reduce((total, session) => total + session.terms.length, 0)}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <i className="fas fa-list-alt text-purple-600 text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-800">Academic Sessions</h3>
          <p className="text-gray-600 text-sm">Manage academic sessions and their terms</p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {academicSessions.map((session) => (
            <div key={session.id} className="p-6 hover:bg-gray-50">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => toggleSessionExpansion(session.id)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <i className={`fas fa-chevron-${expandedSessions.has(session.id) ? 'up' : 'down'} text-sm`}></i>
                  </button>
                  <div>
                    <h4 className="font-medium text-gray-900 text-lg">{session.academic_session}</h4>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(session.academic_session_status)}`}>
                        {session.academic_session_status}
                      </span>
                      <span className="text-sm text-gray-500">
                        Created: {new Date(session.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setSelectedSession(session);
                      setShowTermModal(true);
                    }}
                    className="text-sm bg-primary-50 text-primary-700 hover:bg-primary-100 px-3 py-1 rounded flex items-center"
                  >
                    <i className="fas fa-plus mr-1 text-xs"></i> Add Term
                  </button>
                  <button
                    onClick={() => {
                      setEditingSession(session);
                      setShowSessionModal(true);
                    }}
                    className="text-primary-600 hover:text-primary-900"
                    title="Edit"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    onClick={() => handleDeleteSession(session.id)}
                    className="text-red-600 hover:text-red-900"
                    title="Delete"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                  <div className="relative">
                    <select
                      value={session.academic_session_status}
                      onChange={(e) => handleUpdateSessionStatus(session.id, e.target.value)}
                      className="text-sm border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>
              </div>
              
              {expandedSessions.has(session.id) && (
                <div className="mt-4 pl-10">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Terms ({session.terms.length})</h5>
                  {session.terms.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {session.terms.map((term) => (
                        <div key={term.id} className="border rounded-lg p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium">{term.term}</p>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeClass(term.term_status)}`}>
                                  {term.term_status}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <select
                                value={term.term_status}
                                onChange={(e) => handleUpdateTermStatus(term.id, e.target.value, e)}
                                className="text-xs border rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="pending">Pending</option>
                                <option value="published">Published</option>
                              </select>
                              <button
                                onClick={(e) => handleDeleteTerm(term.id, e)}
                                className="text-red-500 hover:text-red-700 text-sm"
                                title="Delete Term"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      <i className="fas fa-inbox text-3xl mb-2"></i>
                      <p>No terms added yet</p>
                      <button
                        onClick={() => {
                          setSelectedSession(session);
                          setShowTermModal(true);
                        }}
                        className="mt-2 text-primary-600 hover:text-primary-700 text-sm"
                      >
                        Add first term
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Session Modal */}
      <Modal
        isOpen={showSessionModal}
        onClose={() => {
          setShowSessionModal(false);
          setEditingSession(null);
        }}
        title={editingSession ? 'Edit Academic Session' : 'Create Academic Session'}
        size="md"
      >
        <Formik
          initialValues={{
            academic_session: editingSession?.academic_session || '',
          }}
          validationSchema={sessionSchema}
          onSubmit={handleSaveSession}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Academic Session Name *
                </label>
                <Field
                  type="text"
                  name="academic_session"
                  className="input-field"
                  placeholder="e.g., 2023-2024"
                />
                {errors.academic_session && touched.academic_session && (
                  <div className="text-red-500 text-sm mt-1">{errors.academic_session}</div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Format: Year-Year (e.g., 2023-2024)
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowSessionModal(false);
                    setEditingSession(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      {editingSession ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Term Modal */}
      <Modal
        isOpen={showTermModal}
        onClose={() => {
          setShowTermModal(false);
          setSelectedSession(null);
        }}
        title={`Add Term to ${selectedSession?.academic_session}`}
        size="md"
      >
        {selectedSession && (
          <Formik
            initialValues={{
              term: '',
            }}
            validationSchema={termSchema}
            onSubmit={handleSaveTerm}
          >
            {({ isSubmitting, errors, touched }) => (
              <Form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Term Name *
                  </label>
                  <Field
                    type="text"
                    name="term"
                    className="input-field"
                    placeholder="e.g., Term 1"
                  />
                  {errors.term && touched.term && (
                    <div className="text-red-500 text-sm mt-1">{errors.term}</div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Examples: Term 1, First Term, Semester 1
                  </p>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">
                    <p className="font-medium">For Academic Session:</p>
                    <p>{selectedSession.academic_session}</p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowTermModal(false);
                      setSelectedSession(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary px-4 py-2"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-plus mr-2"></i>
                        Add Term
                      </>
                    )}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    </div>
  );
};

export default AcademicSessionManagement;